import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type CustomMetricKey, sendCustomMetric} from '@github-ui/stats'
import type {TrustedTypePolicy, TrustedTypePolicyFactory} from 'trusted-types/lib/index'

import type {SafeHTMLString, SanitizedHTML} from './safe-html-string'

// Facade for the standard `@types/trusted-types` DOM types: consumers import the
// Trusted Types policy types from `@github-ui/trusted-types` rather than reaching
// into `trusted-types/lib` directly, keeping the third-party dependency owned by
// this package. The HTML brand types live in `./safe-html-string`.
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {TrustedHTML, TrustedTypePolicy, TrustedTypePolicyFactory} from 'trusted-types/lib/index'

/**
 * The subset of a Trusted Types policy a minter relies on. `createScriptURL`
 * is present on the type even when a minter does not supply one (e.g. an
 * HTML-only passthrough policy) — minters that omit it keep their `getPolicy()`
 * private so the extra surface is never exposed.
 */
type MintingPolicy = Pick<TrustedTypePolicy, 'name' | 'createHTML' | 'createScriptURL'>

export interface CreateTrustedTypesPolicyOptions {
  /** The policy name advertised in the CSP `trusted-types` directive. */
  name: string
  /** Identity-or-transform applied when minting HTML. */
  createHTML: (input: string) => string
  /** Optional script-URL rule; only supply when the consumer requires it. */
  createScriptURL?: (input: string) => string
}

export interface TrustedTypesPolicyBinding {
  /**
   * Lazily create and memoize this binding's Trusted Types policy. Returns
   * null when Trusted Types are unavailable, the shared feature flag is off, or the
   * policy could not be created.
   */
  getPolicy: () => MintingPolicy | null
  /**
   * Mint an HTML string as `TrustedHTML` when the policy is active. Falls back
   * to the input branded as `SafeHTMLString` when Trusted Types are
   * unavailable or the policy could not be created. The input MUST already be
   * verified (trusted server origin, static content, or DOMPurify output).
   */
  mint: (html: string) => SanitizedHTML
  /** Reset the memoized policy. Test-only — not part of the public package API. */
  _resetForTests: () => void
  /**
   * Inject a fake `TrustedTypePolicyFactory` (or `null` to simulate the
   * platform global being absent). Test-only — pass `undefined` to revert to
   * the real platform global.
   */
  _setTrustedTypesForTests: (impl: TrustedTypePolicyFactory | null | undefined) => void
}

/**
 * Build a self-contained Trusted Types policy binding. Each minter calls this
 * once at module load with its own name and rules, and
 * keeps the resulting binding (and its policy) private — only the generic
 * mechanism is shared, preserving each policy's trust boundary.
 */
export function createTrustedTypesPolicy(options: CreateTrustedTypesPolicyOptions): TrustedTypesPolicyBinding {
  const {name, createHTML, createScriptURL} = options
  const tags = {trusted_types_policy_name: name} as const

  let cachedPolicy: MintingPolicy | null | undefined

  // Once the metric sink has thrown, stop calling it for this binding. A broken
  // sink (e.g. a monkey-patched global in the stats/beacon path) is broken for
  // the page lifetime, and `safeMetric` runs on every UnsafeHTML render — so
  // retrying a known-bad call would throw-and-swallow on the hot path forever.
  let metricsDisabled = false

  // Test seam — the real `window.trustedTypes` in browser test runners is a
  // non-configurable accessor that cannot be replaced via vi.stubGlobal.
  let trustedTypesOverride: TrustedTypePolicyFactory | null | undefined

  function resolveTrustedTypes(): TrustedTypePolicyFactory | undefined {
    if (trustedTypesOverride !== undefined) {
      return trustedTypesOverride ?? undefined
    }
    if (typeof window === 'undefined') return undefined
    return (window as unknown as {trustedTypes?: TrustedTypePolicyFactory}).trustedTypes
  }

  // Telemetry is best-effort: a failing metric sink (e.g. a monkey-patched
  // global in the stats/beacon path) must never propagate out of this binding
  // and break sanitization/rendering.
  function safeMetric(metricName: CustomMetricKey): void {
    if (metricsDisabled) return
    try {
      sendCustomMetric({name: metricName, value: 1, tags})
    } catch {
      // Swallow and stop calling the sink — metrics must not affect the
      // sanitization result, and a broken sink stays broken.
      metricsDisabled = true
    }
  }

  function getPolicy(): MintingPolicy | null {
    if (cachedPolicy !== undefined) {
      return cachedPolicy
    }

    const tt = resolveTrustedTypes()
    const flagEnabled = isFeatureEnabled('trusted_types_policies')
    if (!flagEnabled || tt === undefined) {
      cachedPolicy = null
      return cachedPolicy
    }

    try {
      const rules = createScriptURL ? {createHTML, createScriptURL} : {createHTML}
      cachedPolicy = tt.createPolicy(name, rules)
    } catch (err) {
      cachedPolicy = null
      // Duplicate policy creation is the common-and-expected failure mode
      // (multiple bundles, HMR, etc.) — track it separately from real errors.
      const isDuplicate =
        err instanceof TypeError && typeof err.message === 'string' && err.message.toLowerCase().includes('policy')
      safeMetric(isDuplicate ? 'TRUSTED_TYPES_POLICY_INITIALIZED_TWICE' : 'TRUSTED_TYPES_POLICY_ERROR')
    }

    return cachedPolicy ?? null
  }

  function mint(html: string): SanitizedHTML {
    const policy = getPolicy()
    if (policy === null) {
      return html as SafeHTMLString
    }

    try {
      const trusted = policy.createHTML(html)
      safeMetric('TRUSTED_TYPES_POLICY_CALLED')
      return trusted
    } catch {
      safeMetric('TRUSTED_TYPES_POLICY_ERROR')
      return html as SafeHTMLString
    }
  }

  return {
    getPolicy,
    mint,
    _resetForTests() {
      cachedPolicy = undefined
      metricsDisabled = false
    },
    _setTrustedTypesForTests(impl: TrustedTypePolicyFactory | null | undefined) {
      trustedTypesOverride = impl
    },
  }
}
